import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { CreditModel } from '../../models/credit-model';
import { CreditModel as CreditModelSymbol } from '../../models/credit-model';
import type { UsageService } from '../usage-service';
import type { UsageCalculator } from '../usage-calculator';
import { UsageCalculator as UsageCalculatorSymbol } from '../usage-calculator';

/**
 * 用量服务实现
 *
 * 依赖 CreditModel 提供各类功能的剩余用量查询能力
 * 依赖 UsageCalculator 提供用量计算能力
 */
@injectable()
export class UsageServiceImpl implements UsageService {
    constructor(
        @inject(CreditModelSymbol) private readonly _creditModel: CreditModel,
        @inject(UsageCalculatorSymbol) private readonly _usageCalculator: UsageCalculator,
    ) {}

    get hasCnPatentSpecificationUsage(): boolean {
        return this._usageCalculator.calculateTotalRemaining(
            this._creditModel.modelData,
            [
                'cn_patent_specification',
                'unified_patent_specification_usage',
                'unified_usage',
                'unified_pro_usage',
                'unified_free_usage',
            ],
        ) > 0;
    }

    get hasUsPatentSpecificationUsage(): boolean {
        return this._usageCalculator.calculateTotalRemaining(
            this._creditModel.modelData,
            [
                'us_patent_specification',
                'unified_patent_specification_usage',
                'unified_usage',
                'unified_pro_usage',
                'unified_free_usage',
            ],
        ) > 0;
    }

    get hasEuPatentSpecificationUsage(): boolean {
        return this._usageCalculator.calculateTotalRemaining(
            this._creditModel.modelData,
            [
                'eu_patent_specification',
                'unified_patent_specification_usage',
                'unified_usage',
                'unified_pro_usage',
                'unified_free_usage',
            ],
        ) > 0;
    }

    get hasPatentSpecificationUsage(): boolean {
        return this.hasCnPatentSpecificationUsage
            || this.hasUsPatentSpecificationUsage
            || this.hasEuPatentSpecificationUsage;
    }

    get hasNoveltySearchUsage(): boolean {
        const totalRemaining = this._usageCalculator.calculateTotalRemaining(
            this._creditModel.modelData,
            [
                'novelty_search_usage',
                'unified_usage',
                'unified_pro_usage',
                'unified_free_usage',
            ],
        );
        return totalRemaining > 0;
    }

    get hasFtoSearchUsage(): boolean {
        return this._usageCalculator.calculateTotalRemaining(
            this._creditModel.modelData,
            [
                'fto_search',
                'unified_usage',
                'unified_pro_usage',
                'unified_free_usage',
            ],
        ) > 0;
    }
}
